import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  LayoutDashboard,
  Truck,
  BarChart3,
  Settings,
  MapPin,
  FileText,
  Users,
  AlertCircle,
  Shield,
  Wrench,
  Route as RouteIcon,
} from 'lucide-react'

interface CommandOption {
  icon: React.ElementType
  label: string
  action: () => void
  group: string
}

export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  const commands: CommandOption[] = [
    {
      icon: LayoutDashboard,
      label: 'Dashboard',
      action: () => navigate('/'),
      group: 'Navigation',
    },
    {
      icon: Truck,
      label: 'Live Fleet',
      action: () => navigate('/fleet'),
      group: 'Navigation',
    },
    {
      icon: BarChart3,
      label: 'Analytics',
      action: () => navigate('/analytics'),
      group: 'Navigation',
    },
    {
      icon: Shield,
      label: 'Compliance',
      action: () => navigate('/compliance'),
      group: 'Navigation',
    },
    {
      icon: AlertCircle,
      label: 'Safety',
      action: () => navigate('/safety'),
      group: 'Navigation',
    },
    {
      icon: Wrench,
      label: 'Maintenance',
      action: () => navigate('/maintenance'),
      group: 'Navigation',
    },
    {
      icon: RouteIcon,
      label: 'Routing',
      action: () => navigate('/routing'),
      group: 'Navigation',
    },
    {
      icon: Settings,
      label: 'Settings',
      action: () => navigate('/settings'),
      group: 'Navigation',
    },
    {
      icon: MapPin,
      label: 'Live Map',
      action: () => navigate('/'),
      group: 'Quick Actions',
    },
    {
      icon: FileText,
      label: 'ELD Logs',
      action: () => navigate('/eld'),
      group: 'Quick Actions',
    },
    {
      icon: Users,
      label: 'Manage Drivers',
      action: () => navigate('/drivers'),
      group: 'Quick Actions',
    },
    {
      icon: Shield,
      label: 'View Alerts',
      action: () => navigate('/'),
      group: 'Quick Actions',
    },
  ]

  const handleSelect = (command: CommandOption) => {
    command.action()
    setOpen(false)
  }

  // Group commands
  const navigationCommands = commands.filter((c) => c.group === 'Navigation')
  const quickActionCommands = commands.filter((c) => c.group === 'Quick Actions')

  return (
    <>
      {/* Keyboard shortcut hint */}
      <button
        onClick={() => setOpen(true)}
        className="hidden lg:flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground border rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
      >
        <span>Search</span>
        <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Type a command or search..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>

          <CommandGroup heading="Navigation">
            {navigationCommands.map((command) => {
              const Icon = command.icon
              return (
                <CommandItem
                  key={command.label}
                  onSelect={() => handleSelect(command)}
                  className="cursor-pointer"
                >
                  <Icon className="mr-2 h-4 w-4" />
                  <span>{command.label}</span>
                </CommandItem>
              )
            })}
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading="Quick Actions">
            {quickActionCommands.map((command) => {
              const Icon = command.icon
              return (
                <CommandItem
                  key={command.label}
                  onSelect={() => handleSelect(command)}
                  className="cursor-pointer"
                >
                  <Icon className="mr-2 h-4 w-4" />
                  <span>{command.label}</span>
                </CommandItem>
              )
            })}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  )
}
